import LockIcon from '@mui/icons-material/Lock';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { CheckboxAdapter } from 'components/enhanced/form/adapters/CheckboxAdapter';
import { DatePickerAdapter } from 'components/enhanced/form/adapters/date-time/DatePickerAdapter';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from 'shared/types/general';
import { ITransportDocument } from '../../../types';
import { supplierService } from 'features/inventory/suppliers/api/SupplierService';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';

interface TransportDocumentFormParams {
  transport_document: ITransportDocument;
  onSubmit: any;
  disableEdit?: boolean;
}

export const TransportDocumentForm = ({
  transport_document,
  onSubmit,
  disableEdit
}: TransportDocumentFormParams) => {
  const { t } = useTranslation();

  const [suppliers, setSuppliers] = useState<Option[]>([]);
  const [warehouses, setWarehouses] = useState<Option[]>([]);
  const [rifMult, setRifMult] = useState(false);

  useEffect(() => {
    supplierService.getAllBaseInformation().then((res) => {
      setSuppliers(
        res.map((supplier) => {
          return {
            ...supplier,
            code: `${supplier.code} - ${supplier.company_name}`
          };
        })
      );
    });
    if (transport_document.lines) {
      transport_document.lines.forEach((line) => {
        if (line.notes && line.notes !== transport_document.notes) {
          setRifMult(true);
          return;
        }
      });
    }

    warehouseService.getAllBaseInformation().then(setWarehouses);
  }, []);

  return (
    <>
      {transport_document && suppliers && (
        <EnhancedForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!transport_document.id}
          disableEdit={disableEdit}
          formId="amountForm"
          autoStyleLayout={true}
          columns={4}>
          <IdentityField
            gridSpan={2}
            name="supplier"
            label={t(`global.supplier`)}
            initialValue={transport_document.supplier}
            initValue={suppliers.find((s) => s.id === transport_document.supplier)}
            component={AutocompleteAdapter}
            options={suppliers}
            disabled={disableEdit}
            required
          />
          <IdentityField
            gridSpan={2}
            name="warehouse"
            label={t(`global.warehouse`)}
            initialValue={transport_document.warehouse}
            initValue={warehouses.find((s) => s.id === transport_document.warehouse)}
            component={AutocompleteAdapter}
            options={warehouses}
            disabled={disableEdit}
            type="text"
          />
          {/* Seconda riga: Ref, Date, Date Arrival, Closed */}
          <IdentityField
            name="ref"
            label={t(`entities.ddt.ref`)}
            initialValue={transport_document.ref}
            component={TextFieldAdapter}
            disabled={disableEdit}
            type="text"
          />
          <IdentityField
            name="date"
            label={t(`entities.ddt.date`)}
            component={DatePickerAdapter}
            type="date"
            disabled={disableEdit}
            initialValue={transport_document?.date ?? null}
            noFuture={true}
          />
          <IdentityField
            name="date_arrival"
            label={t(`entities.ddt.date_arrival`)}
            component={DatePickerAdapter}
            type="date"
            disabled={disableEdit}
            initialValue={transport_document?.date_arrival ?? null}
            noFuture={true}
          />
          <IdentityField
            name="closed"
            label={transport_document?.closed ? t(`entities.ddt.closed`) : t(`entities.ddt.open`)}
            component={CheckboxAdapter}
            type="checkbox"
            initialValue={transport_document?.closed ?? false}
            icon={<LockOpenOutlinedIcon />}
            checkedIcon={<LockIcon />}
            disabled
          />
          {/* Terza riga: Note */}
          <IdentityField
            gridSpan={4}
            name="notes"
            label={t(`entities.ddt.notes`)}
            disabled={disableEdit}
            initialValue={transport_document.notes}
            component={TextFieldAdapter}
            sx={{
              '& .MuiInputLabel-root': {
                color: rifMult == true ? 'warning.main' : 'inherit' // Imposta il colore dell'etichetta a giallo
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: rifMult == true ? 'warning.main' : 'inherit' // Colore del bordo
                },
                '&:hover fieldset': {
                  borderColor: rifMult == true ? 'warning.main' : 'inherit' // Colore del bordo durante l'hover
                },
                '&.Mui-focused fieldset': {
                  borderColor: rifMult == true ? 'warning.main' : 'inherit' // Colore del bordo quando il campo è in focus
                }
              }
            }}
            type="text"
          />
        </EnhancedForm>
      )}
    </>
  );
};

export default TransportDocumentForm;
